import { useEffect, useRef, useState } from 'react';
import { PDFDocument, degrees } from 'pdf-lib';
import { ArrowLeft, ArrowRight, Plus, RotateCw, Trash2, X } from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes } from '@/lib/pdfIntake';
import { copyPageOverlays, finishPageAssembly } from '@/lib/pageAssembly';
import { useFileDialog } from '@/hooks/useFileDialog';
import PageCanvas from './PageCanvas';

export default function PageOrganizer({ getFile, onApply, onClose }) {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const sources = useRef([]);
  const counter = useRef(0);
  const initialFile = useRef(getFile);
  const { openFiles, saveFile } = useFileDialog();

  async function readSource(file, isCurrent = () => true) {
    const bytes = await readPdfLibBytes(file);
    const doc = await PDFDocument.load(bytes);
    const preview = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
    if (!isCurrent()) { await preview.destroy(); return []; }
    const source = { doc, preview, name: file.name };
    sources.current.push(source);
    return doc.getPages().map((page, index) => ({ id: ++counter.current, source, pageNum: index + 1,
      width: page.getWidth(), height: page.getHeight(), rotation: 0 }));
  }

  useEffect(() => {
    let cancelled = false;
    initialFile.current().then((file) => readSource(file, () => !cancelled)).then((items) => {
      if (!cancelled) setPages(items);
    }).catch((err) => { if (!cancelled) setError(err.message); }).finally(() => { if (!cancelled) setBusy(false); });
    return () => {
      cancelled = true;
      sources.current.forEach((source) => source.preview.destroy());
      sources.current = [];
    };
  }, []);

  function move(index, step) {
    setPages((current) => {
      const next = [...current];
      [next[index], next[index + step]] = [next[index + step], next[index]];
      return next;
    });
  }
  async function insertFiles() {
    setBusy(true); setError(null);
    try {
      const files = await openFiles('.pdf');
      if (!files.length) return;
      for (const file of files) {
        const items = await readSource(new File([file.buffer], file.name, { type: 'application/pdf' }));
        setPages((current) => [...current, ...items]);
      }
    } catch (err) { setError(`Could not add file: ${err.message}`); }
    finally { setBusy(false); }
  }
  async function assemble(items) {
    const doc = await PDFDocument.create();
    const overlays = { edits: [], annotations: [] };
    const copiedPages = new Map();
    for (const source of new Set(items.map((item) => item.source).filter(Boolean))) {
      const group = items.filter((item) => item.source === source);
      const copied = await doc.copyPages(source.doc, group.map((item) => item.pageNum - 1));
      group.forEach((item, index) => copiedPages.set(item.id, copied[index]));
    }
    for (const item of items) {
      if (!item.source) { doc.addPage([item.width, item.height]).setRotation(degrees(item.rotation)); continue; }
      const offset = doc.getPageCount();
      const page = copiedPages.get(item.id);
      doc.addPage(page);
      page.setRotation(degrees((page.getRotation().angle + item.rotation) % 360));
      const copied = await copyPageOverlays(item.source.doc, [item.pageNum], offset, `page-${item.id}`);
      overlays.edits.push(...copied.edits);
      overlays.annotations.push(...copied.annotations);
    }
    return finishPageAssembly(doc, overlays);
  }
  async function finish(extract = false) {
    setBusy(true); setError(null);
    try {
      const bytes = await assemble(extract ? pages.filter((page) => selected.has(page.id)) : pages);
      if (extract) await saveFile(new Blob([bytes], { type: 'application/pdf' }), 'extracted-pages.pdf');
      else await onApply(bytes);
    } catch (err) { setError(`Could not prepare pages: ${err.message}`); }
    finally { setBusy(false); }
  }

  return <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4" onKeyDown={(e) => e.stopPropagation()}>
    <section role="dialog" aria-modal="true" aria-label="Organize pages" className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
      <header className="px-6 py-4 border-b border-gray-200 flex items-center justify-between"><div><h2 className="font-semibold text-lg text-gray-900">Organize pages</h2><p className="text-sm text-gray-500">Add, remove, rotate, and reorder pages in this document.</p></div><button aria-label="Close page organizer" disabled={busy} onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={20} /></button></header>
      <div className="px-6 py-3 border-b border-gray-200 flex flex-wrap gap-2">
        <button disabled={busy} onClick={insertFiles} className="px-3 py-2 rounded-lg border text-sm flex items-center gap-2"><Plus size={16} />Insert PDF files</button>
        <button disabled={busy} onClick={() => setPages((current) => [...current, { id: ++counter.current, width: current[0]?.width || 612, height: current[0]?.height || 792, rotation: 0 }])} className="px-3 py-2 rounded-lg border text-sm">Add blank page</button>
        <button disabled={busy || !selected.size || selected.size === pages.length} onClick={() => { setPages((current) => current.filter((item) => !selected.has(item.id))); setSelected(new Set()); }} className="px-3 py-2 rounded-lg border text-sm text-red-600 disabled:opacity-40">Delete selected</button>
        <button disabled={busy || !selected.size} onClick={() => finish(true)} className="px-3 py-2 rounded-lg border text-sm disabled:opacity-40">Extract selected</button>
        <span className="ml-auto self-center text-sm text-gray-500">{pages.length} pages · {selected.size} selected</span>
      </div>
      {error && <p role="alert" className="mx-6 mt-3 text-sm text-red-600">{error}</p>}
      <div className="p-6 overflow-auto flex-1 min-h-0 bg-gray-50">
        {busy && <p role="status" className="mb-3 text-sm text-brand-blue">Preparing pages…</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {pages.map((item, index) => <div key={item.id} className={`bg-white border rounded-xl p-2 ${selected.has(item.id) ? 'ring-2 ring-brand-blue' : 'border-gray-200'}`}>
            <label className="flex items-center gap-2 mb-2 text-sm"><input type="checkbox" disabled={busy} checked={selected.has(item.id)} aria-label={`Select page ${index + 1}`} onChange={() => setSelected((current) => { const next = new Set(current); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); return next; })} />Page {index + 1}</label>
            <div className="h-40 flex items-center justify-center overflow-hidden bg-gray-100" style={{ transform: `rotate(${item.rotation}deg)` }}>{item.source ? <PageCanvas pdfDoc={item.source.preview} pageNumber={item.pageNum} scale={Math.min(140 / item.width, 150 / item.height)} /> : <span className="text-xs text-gray-400">Blank page</span>}</div>
            <div className="flex items-center justify-between pt-2">
              <button aria-label={`Move page ${index + 1} earlier`} disabled={busy || index === 0} onClick={() => move(index, -1)} className="p-2 disabled:opacity-25"><ArrowLeft size={15} /></button>
              <button aria-label={`Rotate page ${index + 1}`} disabled={busy} onClick={() => setPages((current) => current.map((page) => page.id === item.id ? { ...page, rotation: (page.rotation + 90) % 360 } : page))} className="p-2"><RotateCw size={15} /></button>
              <button aria-label={`Delete page ${index + 1}`} disabled={busy || pages.length === 1} onClick={() => { setPages((current) => current.filter((page) => page.id !== item.id)); setSelected((current) => { const next = new Set(current); next.delete(item.id); return next; }); }} className="p-2 text-red-600 disabled:opacity-25"><Trash2 size={15} /></button>
              <button aria-label={`Move page ${index + 1} later`} disabled={busy || index === pages.length - 1} onClick={() => move(index, 1)} className="p-2 disabled:opacity-25"><ArrowRight size={15} /></button>
            </div>
          </div>)}
        </div>
      </div>
      <footer className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3"><button disabled={busy} onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button><button disabled={busy || !pages.length} onClick={() => finish()} className="px-4 py-2 rounded-lg bg-brand-blue text-white font-medium disabled:opacity-50">Apply to document</button></footer>
    </section>
  </div>;
}
